import { cx } from '@emotion/css';
import React, { FC, ReactNode } from 'react';

export type CardVariant = 'elevated' | 'outlined' | 'flat';

type CardHeaderProps = {
  children: ReactNode;
  className?: string;
};

type CardFooterProps = {
  children: ReactNode;
  className?: string;
};

type CardContentProps = {
  children: ReactNode;
  className?: string;
};

/**
 * CardHeader component for card composition pattern.
 * Provides consistent header styling with border separator.
 *
 * @example
 * <Card>
 *   <CardHeader>Title</CardHeader>
 *   <CardContent>Content</CardContent>
 * </Card>
 */
export const CardHeader: FC<CardHeaderProps> = ({ children, className }) => (
  <div className={cx('px-6 py-4 border-b border-gray-200', className)}>
    {children}
  </div>
);

/**
 * CardFooter component for card composition pattern.
 * Provides consistent footer styling with border separator.
 *
 * @example
 * <Card>
 *   <CardContent>Content</CardContent>
 *   <CardFooter>Footer</CardFooter>
 * </Card>
 */
export const CardFooter: FC<CardFooterProps> = ({ children, className }) => (
  <div className={cx('px-6 py-4 border-t border-gray-200', className)}>
    {children}
  </div>
);

/**
 * CardContent component for card composition pattern.
 * Provides consistent content area styling.
 *
 * @example
 * <Card>
 *   <CardHeader>Title</CardHeader>
 *   <CardContent>Main content</CardContent>
 *   <CardFooter>Footer</CardFooter>
 * </Card>
 */
export const CardContent: FC<CardContentProps> = ({ children, className }) => (
  <div className={cx('px-6 py-4', className)}>{children}</div>
);

type Props = {
  children?: ReactNode;
  className?: string;
  /**
   * Custom shadow color (legacy prop).
   * @deprecated Use variant="elevated" instead. This prop is kept for backward compatibility.
   */
  boxShadowColor?: string;
  /**
   * Card variant style.
   * - 'elevated': Default variant with shadow elevation
   * - 'outlined': Border instead of shadow
   * - 'flat': No shadow or border
   * @default 'elevated'
   */
  variant?: CardVariant;
};

const getVariantStyles = (
  variant: CardVariant,
  boxShadowColor?: string
): { className: string; style?: React.CSSProperties } => {
  // If boxShadowColor is provided, use legacy behavior (elevated with custom color)
  if (boxShadowColor) {
    return {
      className: 'bg-white',
      style: {
        boxShadow: `${boxShadowColor} 0px 0px 2px 0px, ${boxShadowColor} 0px 12px 24px -4px`,
      },
    };
  }

  switch (variant) {
    case 'elevated':
      return {
        className: 'bg-white',
        style: {
          boxShadow:
            'rgba(145, 158, 171, 0.12) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
        },
      };
    case 'outlined':
      return {
        className: 'bg-white border border-gray-200',
      };
    case 'flat':
      return {
        className: 'bg-white',
      };
    default:
      return {
        className: 'bg-white',
        style: {
          boxShadow:
            'rgba(145, 158, 171, 0.12) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
        },
      };
  }
};

const isCardComposition = (child: any): boolean => {
  if (!child) return false;
  // Check if it's a React element with CardHeader, CardFooter, or CardContent type
  if (
    child.type === CardHeader ||
    child.type === CardFooter ||
    child.type === CardContent
  )
    return true;
  // Handle React.Children.map case
  if (typeof child === 'object' && 'type' in child) {
    return (
      child.type === CardHeader ||
      child.type === CardFooter ||
      child.type === CardContent
    );
  }
  return false;
};

/**
 * Card component with multiple variants and composition support.
 *
 * ## Variants
 * - `elevated`: Default variant with shadow (default)
 * - `outlined`: Border instead of shadow
 * - `flat`: No shadow or border
 *
 * ## Composition Pattern
 * Use CardHeader, CardContent, and CardFooter for structured layouts:
 *
 * ```tsx
 * <Card variant="elevated">
 *   <CardHeader>Title</CardHeader>
 *   <CardContent>Main content</CardContent>
 *   <CardFooter>Footer</CardFooter>
 * </Card>
 * ```
 *
 * ## Backward Compatibility
 * The component maintains backward compatibility with existing props:
 * - `boxShadowColor` prop still works (legacy)
 * - `className` prop for custom styling
 * - Default padding when not using composition pattern
 *
 * ## Responsive Behavior
 * Cards automatically adapt to container width. Padding and spacing
 * adjust based on screen size when using Tailwind responsive classes.
 *
 * @example
 * // Simple card
 * <Card variant="elevated">
 *   <div>Content</div>
 * </Card>
 *
 * @example
 * // Card with composition
 * <Card variant="outlined">
 *   <CardHeader>Title</CardHeader>
 *   <CardContent>Content</CardContent>
 * </Card>
 */
export const Card: FC<Props> = ({
  children,
  className,
  boxShadowColor,
  variant = 'elevated',
}) => {
  const variantStyles = getVariantStyles(variant, boxShadowColor);

  // Check if children contain CardHeader, CardFooter, or CardContent (composition pattern)
  const hasComposition =
    React.Children.toArray(children).some(isCardComposition);

  // If no composition pattern, use default padding
  const defaultPadding = !hasComposition ? 'px-6 py-4' : '';

  return (
    <div
      className={cx(
        'rounded-2xl w-full h-full relative overflow-hidden min-h-[100px]',
        variantStyles.className,
        defaultPadding,
        className
      )}
      style={variantStyles.style}
    >
      {children}
    </div>
  );
};
